import { DEFAULT_BRAND_ID, DEFAULT_COLOR_SCHEME } from "./defaults-i_xG2F_1.js";
import { createContext, useCallback, useEffect, useState } from "react";
//#region src/components/ThemeProvider/themeScopeContext.ts
/**
* What a `ThemeScope` publishes about itself, in its own module so a hook can read it without
* importing the component that provides it.
*
* - `scheme`: the scheme this subtree is pinned to, or `null` outside a scope.
* - `container`: a body-level node carrying the scope's `data-theme` and token vars. A canvas
*   (chart, diagram) that resolves live `--*` values reads them off this rather than `<html>`,
*   which is still painting the page's scheme.
*/
const ThemeScopeSchemeContext = createContext(null);
const ThemeScopeContainerContext = createContext(null);
//#endregion
//#region src/components/ThemeProvider/useBrandState.ts
const BRAND_ATTRIBUTE = "data-brand";
const useBrandState = ({ defaultBrandId: defaultBrand = DEFAULT_BRAND_ID, brandId: controlledBrand, onBrandIdChange: onBrandChange } = {}) => {
	const [uncontrolledBrand, setUncontrolledBrand] = useState(defaultBrand);
	const isControlled = controlledBrand !== void 0;
	const brandId = isControlled ? controlledBrand : uncontrolledBrand;
	const setBrand = useCallback((next) => {
		if (!isControlled) setUncontrolledBrand(next);
		onBrandChange?.(next);
	}, [isControlled, onBrandChange]);
	useEffect(() => {
		if (typeof document === "undefined") return;
		document.documentElement.setAttribute(BRAND_ATTRIBUTE, brandId);
	}, [brandId]);
	return {
		brandId,
		setBrand
	};
};
//#endregion
//#region src/components/ThemeProvider/useColorSchemeState.ts
const DARK_QUERY = "(prefers-color-scheme: dark)";
const THEME_ATTRIBUTE = "data-theme";
const getSystemColorScheme = () => {
	if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
	return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
};
const useColorSchemeState = ({ defaultScheme = DEFAULT_COLOR_SCHEME, scheme: propScheme, onSchemeChange } = {}) => {
	const [uncontrolledScheme, setUncontrolledScheme] = useState(defaultScheme);
	const [systemScheme, setSystemScheme] = useState(getSystemColorScheme);
	const isControlled = propScheme !== void 0;
	const scheme = isControlled ? propScheme : uncontrolledScheme;
	const setScheme = useCallback((next) => {
		if (!isControlled) setUncontrolledScheme(next);
		onSchemeChange?.(next);
	}, [isControlled, onSchemeChange]);
	useEffect(() => {
		if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
		const mq = window.matchMedia(DARK_QUERY);
		const onChange = () => setSystemScheme(mq.matches ? "dark" : "light");
		onChange();
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, []);
	const resolvedScheme = scheme === "system" ? systemScheme : scheme;
	useEffect(() => {
		if (typeof document === "undefined") return;
		document.documentElement.setAttribute(THEME_ATTRIBUTE, resolvedScheme);
	}, [resolvedScheme]);
	return {
		scheme,
		resolvedScheme,
		setScheme
	};
};
//#endregion
export { BRAND_ATTRIBUTE, THEME_ATTRIBUTE, ThemeScopeContainerContext, ThemeScopeSchemeContext, useBrandState, useColorSchemeState };

//# sourceMappingURL=useColorSchemeState-Cw4MCXUs.js.map